import React, { useState } from 'react';
import axios from 'axios';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OnboardingModal from './OnboardingModal';
import Logo from './Logo';
import MobileBottomBar from './MobileBottomBar';
import { Sheet, SheetContent } from './ui/sheet';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, ClipboardList, DollarSign, FileText,
  MapPin, Users, Settings, Upload, ShieldCheck,
  Menu, LogOut, Building2, X, Calculator,
  LayoutGrid, Clock, LogIn, Zap, Settings2, Target, Phone,
  ChevronRight, Bell, CreditCard
} from 'lucide-react';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Data Entry',
    items: [
      { icon: ClipboardList, label: 'EOD Summary (Sales)', path: '/eod/new', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Phone, label: 'EOD Summary (Call Center)', path: '/eod/callcenter', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: DollarSign, label: 'Record Sale', path: '/sales/new', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Users, label: 'Record Show', path: '/shows/new', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Records',
    items: [
      { icon: FileText, label: 'EOD Records', path: '/eod/records', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: FileText, label: 'Sales Records', path: '/sales/records', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Analytics',
    items: [
      { icon: Zap, label: 'Flow Calculator', path: '/calculator/flow', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Calculator, label: 'Marketing ROI', path: '/marketing/calculator', roles: ['admin', 'super_admin'] },
      { icon: LayoutGrid, label: 'Location Compare', path: '/locations/compare', roles: ['admin', 'super_admin'] },
      { icon: Target, label: 'Monthly Goals', path: '/goals', roles: ['admin', 'super_admin'] },
    ]
  },
  {
    label: 'Administration',
    items: [
      { icon: MapPin, label: 'Locations', path: '/admin/locations', roles: ['admin', 'super_admin'] },
      { icon: Users, label: 'Users', path: '/admin/users', roles: ['admin', 'super_admin'] },
      { icon: Settings, label: 'Dropdowns', path: '/admin/dropdowns', roles: ['admin', 'super_admin'] },
      { icon: Upload, label: 'Import CSV', path: '/admin/import', roles: ['admin', 'super_admin'] },
      { icon: Clock, label: 'Audit Log', path: '/admin/audit-log', roles: ['admin', 'super_admin'] },
      { icon: Bell, label: 'Notifications', path: '/admin/notifications', roles: ['admin', 'super_admin'] },
    ]
  },
  {
    label: 'Super Admin',
    items: [
      { icon: ShieldCheck, label: 'Organizations', path: '/superadmin', roles: ['super_admin'] },
    ]
  },
  {
    label: 'Account',
    items: [
      { icon: CreditCard, label: 'Subscription', path: '/subscription', roles: ['admin', 'super_admin'] },
      { icon: Settings2, label: 'Settings', path: '/settings', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  }
];

const NavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onClick(item.path)}
      className={`sidebar-nav-item w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left mb-0.5 ${isActive ? 'active' : ''}`}
      data-testid={`sidebar-nav-item-${item.path.replace(/\//g, '-')}`}
    >
      <Icon size={15} className="flex-shrink-0 opacity-80" />
      <span className="flex-1 truncate">{item.label}</span>
      {isActive && <div className="w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />}
    </button>
  );
};

const SidebarContent = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNav = (path) => { navigate(path); if (onClose) onClose(); };
  const handleLogout = () => { logout(); navigate('/login'); };

  const filteredGroups = navGroups.map(g => ({
    ...g,
    items: g.items.filter(i => i.roles.includes(user?.role))
  })).filter(g => g.items.length > 0);

  const isActive = (path) => path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(path);

  const roleLabel = user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Staff';

  return (
    <div className="flex flex-col h-full sidebar-bg sidebar-noise relative">
      {/* Logo area */}
      <div className="relative z-10 flex items-center justify-between px-4 py-4 border-b border-white/8">
        <Logo size={34} showText={true} light={true} />
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-white/5">
            <X size={15} />
          </button>
        )}
      </div>

      {/* User pill */}
      <div className="relative z-10 px-3 py-3 border-b border-white/8">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-white text-xs"
            style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}>
            {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate leading-tight">
              {user?.full_name || user?.username}
            </p>
            <p className="text-slate-500 text-xs leading-tight">{roleLabel}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex-1 overflow-y-auto px-2 py-3">
        {filteredGroups.map((group) => (
          <div key={group.label} className="mb-5">
            <p className="section-label px-3 mb-1.5">{group.label}</p>
            {group.items.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                isActive={isActive(item.path)}
                onClick={handleNav}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Sign out */}
      <div className="relative z-10 px-2 py-3 border-t border-white/8">
        <button
          onClick={handleLogout}
          className="sidebar-nav-item w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left"
          data-testid="logout-button"
        >
          <LogOut size={15} className="opacity-80" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const impersonating = (() => {
    try { return JSON.parse(localStorage.getItem('arch_impersonating')); } catch { return null; }
  })();

  const exitImpersonation = () => {
    const saToken = localStorage.getItem('arch_sa_token');
    if (saToken) {
      localStorage.setItem('arch_token', saToken);
      localStorage.removeItem('arch_sa_token');
      localStorage.removeItem('arch_impersonating');
      axios.defaults.headers.common['Authorization'] = `Bearer ${saToken}`;
      window.location.href = '/superadmin';
    }
  };

  // Derive page title from location
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path.includes('/eod/new')) return 'EOD Summary — Sales';
    if (path.includes('/eod/callcenter')) return 'EOD Summary — Call Center';
    if (path.includes('/sales/new')) return 'Record Sale';
    if (path.includes('/eod/records')) return 'EOD Records';
    if (path.includes('/sales/records')) return 'Sales Records';
    if (path.includes('/marketing')) return 'Marketing ROI';
    if (path.includes('/locations/compare')) return 'Location Compare';
    if (path.includes('/goals')) return 'Monthly Goals';
    if (path.includes('/calculator/flow')) return 'Flow Calculator';
    if (path.includes('/admin/locations')) return 'Locations';
    if (path.includes('/admin/users')) return 'Users';
    if (path.includes('/admin/dropdowns')) return 'Dropdown Settings';
    if (path.includes('/admin/import')) return 'Import Data';
    if (path.includes('/admin/audit-log')) return 'Audit Log';
    if (path.includes('/superadmin')) return 'Organizations';
    if (path.includes('/settings')) return 'Settings';
    return 'Full Arch ROI';
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'hsl(210, 40%, 98%)' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 flex-shrink-0"
        style={{ borderRight: '1px solid rgba(255,255,255,0.06)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64" style={{ background: '#0A0F1E', border: 'none' }}>
          <SidebarContent onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top header */}
        <header className="flex items-center gap-4 px-6 py-3.5 flex-shrink-0 page-header">
          <button
            className="lg:hidden text-slate-500 hover:text-slate-800 p-1"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading font-semibold text-slate-900 text-base tracking-tight">{getPageTitle()}</h1>
          </div>
          {/* Impersonation chip */}
          {impersonating && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
              <LogIn size={12} />
              <span>Viewing: <strong>{impersonating.org_name}</strong></span>
              <button onClick={exitImpersonation} className="ml-1 hover:text-amber-900 font-bold">Exit ✕</button>
            </div>
          )}
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}>
              {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="px-4 sm:px-6 py-6"
          >
            <Outlet />
          </motion.div>
        </main>
        
        {/* Mobile Bottom Tab Bar */}
        <MobileBottomBar />
      </div>

      {/* Onboarding modal */}
      {user && user.role === 'admin' && <OnboardingModal user={user} />}
    </div>
  );
}
